import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { tradingService } from '../../services/tradingService';

// Infusion Manager — see/manage an account's external capital events (infusions /
// withdrawals). These anchor account-level performance: inception = the first inflow, and
// every flow is neutralised in the account TWR. Editing a flow re-anchors the curve.
// SPLIT (wishlist #27, owner only): a deposit can be credited straight to the account's baskets (by %), so it does not
// sit as unowned cash — capital only, no trade now; deleting a split deposit gives the credit back.
const fmt$ = (n) =>
  (n == null || isNaN(n)) ? '—'
    : `${n < 0 ? '−' : ''}$${Math.abs(Number(n)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const InfusionManagerPanel = ({ accountId, accountName }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [date, setDate] = useState('');
  const [amount, setAmount] = useState('');
  const [direction, setDirection] = useState('in'); // 'in' | 'out'
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [baskets, setBaskets] = useState([]);       // the account's baskets (members on this account)
  const [split, setSplit] = useState({});           // {portfolioId: pct} — empty = keep as account cash
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!accountId) { setBaskets([]); return; }
    tradingService.listPortfolios()
      .then((r) => {
        const d = Array.isArray(r.data) ? r.data : (r.data.results || []);
        setBaskets(d.filter((p) => p.is_active && (p.members || []).some((m) => String(m.account) === String(accountId)))
          .map((p) => ({ id: p.id, name: p.name, method: p.optimizer_method })));
      })
      .catch(() => setBaskets([]));
    setSplit({});
  }, [accountId]);
  const splitTotal = Object.values(split).reduce((a, b) => a + (parseFloat(b) || 0), 0);

  const load = useCallback(() => {
    if (!accountId) { setRows([]); return; }
    setLoading(true); setErr('');
    tradingService.listAccountCashFlows(accountId)
      .then((r) => { const d = r.data; setRows(Array.isArray(d) ? d : (d.results || [])); })
      .catch(() => setErr('Could not load cash flows.'))
      .finally(() => setLoading(false));
  }, [accountId]);
  useEffect(() => { load(); }, [load]);

  // Chronological running balance (inflows +, withdrawals −).
  const withBalance = useMemo(() => {
    let bal = 0;
    return [...rows]
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.id - b.id))
      .map((r) => { bal += Number(r.amount); return { ...r, balance: bal }; });
  }, [rows]);
  const total = withBalance.length ? withBalance[withBalance.length - 1].balance : 0;

  const add = async () => {
    const amt = parseFloat(amount);
    if (!date) { setErr('Pick a date.'); return; }
    if (isNaN(amt) || amt <= 0) { setErr('Amount must be a positive number.'); return; }
    setBusy(true); setErr('');
    const signed = direction === 'out' ? -Math.abs(amt) : Math.abs(amt);
    const splitRows = direction === 'in'
      ? Object.entries(split).filter(([, v]) => parseFloat(v) > 0).map(([pid, v]) => ({ portfolio: Number(pid), pct: parseFloat(v) }))
      : [];
    if (splitRows.length && Math.abs(splitTotal - 100) > 0.01) { setErr('Split percentages must add up to 100.'); setBusy(false); return; }
    try {
      const r = await tradingService.createAccountCashFlow({
        account: accountId, date, amount: signed.toFixed(4),
        kind: direction === 'out' ? 'external_withdraw' : 'external_topup',
        reason: reason.trim(),
        ...(splitRows.length ? { split: splitRows } : {}),
      });
      const sp = r?.data?.split;
      setMsg(sp ? `Deposit recorded and credited to ${sp.baskets.length} basket(s) — ${sp.flows} strategies, dated ${sp.session}. No trades now.` : '');
      setAmount(''); setReason(''); setSplit({}); load();
    } catch (e) {
      const d = e?.response?.data;
      setErr((d && (d.split || d.account || d.kind || d.amount || d.detail)) || 'Could not save the cash flow.');
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this cash flow? Account inception + performance recompute from the remaining flows. If it was split across baskets, those strategies give the credit back (dated the next session).')) return;
    try { await tradingService.deleteAccountCashFlow(id); load(); }
    catch { setErr('Could not delete the cash flow.'); }
  };

  const inputCls = 'rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#161b22] px-2.5 py-1.5 text-sm text-gray-900 dark:text-gray-100';

  return (
    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex items-baseline justify-between">
        <div>
          <h3 className="text-base font-serif text-gray-900 dark:text-gray-100">Infusion Manager</h3>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 uppercase tracking-wider">
            {accountName || 'account'} · capital in / out · anchors inception
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Net capital</div>
          <div className={`text-lg font-semibold tabular-nums ${total >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{fmt$(total)}</div>
        </div>
      </div>

      {err && <div className="mx-5 mt-3 rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-xs text-red-600 dark:text-red-400">{err}</div>}

      {/* add row */}
      <div className="px-5 py-3 flex flex-wrap items-end gap-2 border-b border-gray-100 dark:border-gray-800/60">
        <label className="flex flex-col gap-0.5"><span className="text-[9px] uppercase tracking-wider text-gray-500">Date</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} /></label>
        <label className="flex flex-col gap-0.5"><span className="text-[9px] uppercase tracking-wider text-gray-500">Direction</span>
          <select value={direction} onChange={(e) => setDirection(e.target.value)} className={inputCls}>
            <option value="in">Infusion (in)</option><option value="out">Withdrawal (out)</option>
          </select></label>
        <label className="flex flex-col gap-0.5"><span className="text-[9px] uppercase tracking-wider text-gray-500">Amount (USD)</span>
          <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" placeholder="0.00" className={`${inputCls} w-36`} /></label>
        <label className="flex flex-col gap-0.5 flex-1 min-w-[8rem]"><span className="text-[9px] uppercase tracking-wider text-gray-500">Note (optional)</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. initial Bloomberg cash" className={inputCls} /></label>
        <button onClick={add} disabled={busy || !accountId}
          className="px-4 py-1.5 text-xs rounded-md bg-brand-gold text-black font-medium hover:bg-brand-gold/90 disabled:opacity-40">
          {busy ? 'Saving…' : 'Add flow'}
        </button>
      </div>
      {direction === 'in' && baskets.length > 0 && (
        <div className="px-5 py-2.5 border-b border-gray-100 dark:border-gray-800/60">
          <div className="text-[9px] uppercase tracking-wider text-gray-500 mb-1.5">
            Split across baskets (optional — leave empty to keep it as account cash) · {splitTotal ? `${splitTotal.toFixed(1)}%` : '—'}
          </div>
          <div className="flex flex-wrap gap-2">
            {baskets.map((b) => (
              <label key={b.id} className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                <span className="truncate max-w-[14rem]" title={b.name}>{b.name}</span>
                <input value={split[b.id] ?? ''} onChange={(e) => setSplit({ ...split, [b.id]: e.target.value })}
                  inputMode="decimal" placeholder="%" className={`${inputCls} w-16`} />
              </label>
            ))}
          </div>
        </div>
      )}
      {msg && <div className="mx-5 mt-2 rounded-md border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-950/30 px-3 py-2 text-xs text-green-600 dark:text-green-400">{msg}</div>}

      {/* table */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-100 dark:border-gray-800/60">
              <th className="text-left font-medium px-5 py-2">Date</th>
              <th className="text-left font-medium px-3 py-2">Kind</th>
              <th className="text-right font-medium px-3 py-2">Amount</th>
              <th className="text-right font-medium px-3 py-2">Balance</th>
              <th className="text-left font-medium px-3 py-2">Note</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-5 py-6 text-center text-gray-400 text-xs">Loading…</td></tr>}
            {!loading && withBalance.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-6 text-center text-gray-400 text-xs">No capital flows yet — add the first infusion above.</td></tr>
            )}
            {withBalance.map((r) => (
              <tr key={r.id} className="border-b border-gray-50 dark:border-gray-800/40 hover:bg-gray-50 dark:hover:bg-gray-800/30">
                <td className="px-5 py-2 tabular-nums text-gray-700 dark:text-gray-300">{r.date}</td>
                <td className="px-3 py-2 text-[11px] text-gray-500">{r.kind === 'external_withdraw' ? 'Withdrawal' : 'Infusion'}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${Number(r.amount) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{fmt$(Number(r.amount))}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmt$(r.balance)}</td>
                <td className="px-3 py-2 text-gray-500 truncate max-w-[16rem]">{r.reason || '—'}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => del(r.id)} className="text-[11px] text-gray-400 hover:text-red-500">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InfusionManagerPanel;
